/**
 * ColorTracker.ts
 * Nhận diện nam châm V4: Sử dụng Image Moments & PCA để bắt trục chính (Main Axis)
 * Giúp định vị chính xác hướng nam châm hình chữ nhật
 */

export interface Point {
    x: number
    y: number
}

interface ColorBlob {
    points: Point[]
    minX: number
    maxX: number
    minY: number
    maxY: number
    centerX: number
    centerY: number
    area: number
}

export interface MagnetPosition {
    north: Point | null // Red pole center (Corrected)
    south: Point | null // Blue pole center (Corrected)
    center: Point | null
    angle: number       // Góc nghiêng chính xác của trục nam châm
    length: number
    isValid: boolean
    width?: number      // Chiều rộng ước tính
}

class ColorTracker {
    // Red ranges (HSV)
    private redHueRanges = [
        { min: 330, max: 360 },
        { min: 0, max: 15 }
    ]

    // Blue ranges (HSV)
    private blueHueRange = { min: 190, max: 250 }

    // Thresholds
    private minSaturation = 0.35 // Giảm nhẹ để bắt tốt hơn
    private minValue = 0.20

    // Grid settings
    private gridSize = 8 // Giảm grid size để tăng độ mịn phân giải
    private minBlobArea = 15

    detect(ctx: CanvasRenderingContext2D, width: number, height: number): MagnetPosition {
        const imageData = ctx.getImageData(0, 0, width, height)
        const data = imageData.data
        const cols = Math.ceil(width / this.gridSize)
        const rows = Math.ceil(height / this.gridSize)
        const grid = new Uint8Array(cols * rows)

        // 1. Grid Scanning
        for (let gy = 0; gy < rows; gy++) {
            for (let gx = 0; gx < cols; gx++) {
                const px = gx * this.gridSize + Math.floor(this.gridSize / 2)
                const py = gy * this.gridSize + Math.floor(this.gridSize / 2)

                if (px >= width || py >= height) continue

                const i = (py * width + px) * 4
                const r = data[i], g = data[i + 1], b = data[i + 2]

                // Filters
                if (r < 40 && g < 40 && b < 40) continue
                if (r > 240 && g > 240 && b > 240) continue

                const { h, s, v } = this.rgbToHsv(r, g, b)

                if (s >= this.minSaturation && v >= this.minValue) {
                    if ((h >= this.redHueRanges[0].min && h <= this.redHueRanges[0].max) ||
                        (h >= this.redHueRanges[1].min && h <= this.redHueRanges[1].max)) {
                        grid[gy * cols + gx] = 1 // Red
                    }
                    else if (h >= this.blueHueRange.min && h <= this.blueHueRange.max) {
                        grid[gy * cols + gx] = 2 // Blue
                    }
                }
            }
        }

        // 2. Blob Detection (Connected Components)
        const redBlobs = this.findBlobs(grid, cols, rows, 1)
        const blueBlobs = this.findBlobs(grid, cols, rows, 2)

        const bestRed = this.getLargestBlob(redBlobs)
        const bestBlue = this.getLargestBlob(blueBlobs)

        const result: MagnetPosition = {
            north: null, south: null, center: null,
            angle: 0, length: 0, isValid: false
        }

        if (bestRed && bestBlue) {
            // Convert grid to pixel coords
            const redCenter = { x: bestRed.centerX * this.gridSize, y: bestRed.centerY * this.gridSize }
            const blueCenter = { x: bestBlue.centerX * this.gridSize, y: bestBlue.centerY * this.gridSize }

            // Basic Distance Check
            const dx = redCenter.x - blueCenter.x
            const dy = redCenter.y - blueCenter.y
            const rawDist = Math.sqrt(dx * dx + dy * dy)

            // Size check logic (như cũ)
            const redSize = Math.sqrt(bestRed.area) * this.gridSize
            const blueSize = Math.sqrt(bestBlue.area) * this.gridSize
            const avgSize = (redSize + blueSize) / 2

            if (rawDist > avgSize * 0.5 && rawDist < avgSize * 4.0) {
                // --- PCA / MOMENTS ANALYSIS ---
                // Gộp tất cả điểm của cả 2 Blob để tính trục chung
                // Điều này giúp loại bỏ sai số cục bộ của từng blob
                const allPoints: Point[] = [...bestRed.points, ...bestBlue.points]

                // Tính trọng tâm chung (Centroid of Magnet)
                let sumX = 0, sumY = 0
                for (const p of allPoints) { sumX += p.x; sumY += p.y }
                const cx = sumX / allPoints.length
                const cy = sumY / allPoints.length
                const centerPixels = { x: cx * this.gridSize, y: cy * this.gridSize }

                // Tính Moments (momen quán tính) để tìm góc trục chính (PCA)
                // Matrix M = [ sum(dx^2)  sum(dx*dy) ]
                //            [ sum(dx*dy) sum(dy^2)  ]
                let mxx = 0, myy = 0, mxy = 0
                for (const p of allPoints) {
                    const devX = p.x - cx
                    const devY = p.y - cy
                    mxx += devX * devX
                    myy += devY * devY
                    mxy += devX * devY
                }

                // Tính góc trục chính (Eigenvector orientation)
                // theta = 0.5 * atan2(2*mxy, mxx - myy)
                let mainAngle = 0.5 * Math.atan2(2 * mxy, mxx - myy)

                // --- Orientation Check ---
                // Góc PCA chỉ là phương (0-180 độ), chưa có chiều (Vector Red->Blue hay Blue->Red?)
                // Ta so sánh với vector nối 2 tâm (Red - Blue) để chọn chiều đúng
                // Vector RedToBlue theo PCA:
                const pcaVecX = Math.cos(mainAngle)
                const pcaVecY = Math.sin(mainAngle)

                // Vector RedToBlue thô:
                // Lưu ý: Bắc(Red), Nam(Blue). Trục thường xét N -> S hoặc ngược lại.
                // Ở đây ta cần vector dọc trục. 

                // Project tâm Red và Blue lên trục PCA để tìm vị trí dọc trục
                const projRed = (redCenter.x - centerPixels.x) * pcaVecX + (redCenter.y - centerPixels.y) * pcaVecY
                const projBlue = (blueCenter.x - centerPixels.x) * pcaVecX + (blueCenter.y - centerPixels.y) * pcaVecY

                // Nếu projBlue < projRed => Chiều PCA đang hướng từ Blue -> Red. 
                // Ta muốn hướng N -> S (Red -> Blue)
                // -> Nếu Proj Red > Proj Blue (nghĩa là Red nằm "dương" hơn Blue trên trục), thì trục đang hướng từ Blue->Red.

                // Let's force Main Angle to points from Red (N) to Blue (S)
                // Vector from Red to Blue roughly:
                const roughDirX = blueCenter.x - redCenter.x
                const roughDirY = blueCenter.y - redCenter.y

                // Dot product with PCA vector
                const dot = roughDirX * pcaVecX + roughDirY * pcaVecY
                if (dot < 0) {
                    mainAngle += Math.PI // Đảo chiều trục
                }

                // Recalculate Unit Vector
                const ux = Math.cos(mainAngle)
                const uy = Math.sin(mainAngle)

                // --- Project Extents (Tìm mép thực của nam châm) ---
                // Tìm điểm xa nhất về phía cực Bắc (Min Proj) và Nam (Max Proj)
                // Lưu ý: Vì trục hướng N->S, thì N sẽ ở phía Âm, S ở phía Dương (so với tâm)

                let minProj = Infinity // Đầu Bắc cực đoan
                let maxProj = -Infinity // Đầu Nam cực đoan

                // Ta chỉ cần quét các điểm biên của Blob
                // Hoặc đơn giản hơn: Project trọng tâm của Red và Blue lên trục, rồi mở rộng ra bằng bán kính ước lượng

                // Project Red Center -> posN_proj
                const posN_proj = (redCenter.x - centerPixels.x) * ux + (redCenter.y - centerPixels.y) * uy
                // Project Blue Center -> posS_proj
                const posS_proj = (blueCenter.x - centerPixels.x) * ux + (blueCenter.y - centerPixels.y) * uy

                // Khoảng cách tâm đo được trên trục chuẩn
                const axisLen = posS_proj - posN_proj // Should be positive if angle is correct

                // Estimated Width (Vuông góc trục)
                // width ~ sqrt(Area / Length) rough approx
                // Hoặc lấy mxx + myy (Trace)

                // --- FINAL RESULT ---
                // Ta đặt cực N và S tại vị trí TRÊN TRỤC CHÍNH (để thẳng hàng)
                // Cách tâm một khoảng = axisLen / 2

                result.center = centerPixels
                result.angle = mainAngle
                result.length = Math.abs(axisLen)

                result.north = {
                    x: centerPixels.x + posN_proj * ux, // ProjN thường âm
                    y: centerPixels.y + posN_proj * uy
                }

                result.south = {
                    x: centerPixels.x + posS_proj * ux, // ProjS thường dương
                    y: centerPixels.y + posS_proj * uy
                }

                result.isValid = true
            }
        }

        return result
    }

    // (Giữ nguyên phần Helper FindBlobs, RGBtoHSV...)
    private findBlobs(grid: Uint8Array, cols: number, rows: number, targetType: number): ColorBlob[] {
        const blobs: ColorBlob[] = []
        const visited = new Uint8Array(cols * rows)

        for (let y = 0; y < rows; y++) {
            for (let x = 0; x < cols; x++) {
                const idx = y * cols + x
                if (grid[idx] === targetType && visited[idx] === 0) {
                    const blob = this.floodFill(grid, visited, cols, rows, x, y, targetType)
                    if (blob.area >= this.minBlobArea) blobs.push(blob)
                }
            }
        }
        return blobs
    }

    private floodFill(grid: Uint8Array, visited: Uint8Array, cols: number, rows: number, startX: number, startY: number, type: number): ColorBlob {
        const points: Point[] = []
        const queue: number[] = [startY * cols + startX]
        visited[startY * cols + startX] = 1

        let minX = startX, maxX = startX
        let minY = startY, maxY = startY
        let sumX = 0, sumY = 0

        while (queue.length > 0) {
            const idx = queue.pop()!
            const cx = idx % cols
            const cy = Math.floor(idx / cols)

            points.push({ x: cx, y: cy })

            if (cx < minX) minX = cx; if (cx > maxX) maxX = cx
            if (cy < minY) minY = cy; if (cy > maxY) maxY = cy
            sumX += cx; sumY += cy

            const neighbors = [
                { nx: cx + 1, ny: cy }, { nx: cx - 1, ny: cy },
                { nx: cx, ny: cy + 1 }, { nx: cx, ny: cy - 1 }
            ]

            for (const n of neighbors) {
                if (n.nx >= 0 && n.nx < cols && n.ny >= 0 && n.ny < rows) {
                    const nIdx = n.ny * cols + n.nx
                    if (visited[nIdx] === 0 && grid[nIdx] === type) {
                        visited[nIdx] = 1; queue.push(nIdx)
                    }
                }
            }
        }
        return {
            points, minX, maxX, minY, maxY,
            centerX: sumX / points.length,
            centerY: sumY / points.length,
            area: points.length
        }
    }

    private getLargestBlob(blobs: ColorBlob[]): ColorBlob | null {
        if (blobs.length === 0) return null
        return blobs.reduce((max, b) => b.area > max.area ? b : max, blobs[0])
    }

    private rgbToHsv(r: number, g: number, b: number) {
        r /= 255; g /= 255; b /= 255
        const max = Math.max(r, g, b), min = Math.min(r, g, b)
        const d = max - min
        let h = 0
        const s = max === 0 ? 0 : d / max
        const v = max
        if (max !== min) {
            switch (max) {
                case r: h = (g - b) / d + (g < b ? 6 : 0); break
                case g: h = (b - r) / d + 2; break
                case b: h = (r - g) / d + 4; break
            }
            h /= 6
        }
        return { h: h * 360, s, v }
    }
}

export default new ColorTracker()
